import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { useSidebar } from '../../contexts/SidebarContext';
import { 
  LayoutDashboard, 
  ArrowDownCircle, 
  ArrowUpCircle,
  TrendingUp,
  RefreshCcw,
  Users,
  FileText,
  Settings,
  ChevronDown,
  FolderTree,
  Building2,
  Wallet,
  FileBarChart,
  ShoppingCart,
  Package,
  CreditCard,
  ClipboardList,
  Link2,
  Target,
  Calculator,
  Receipt,
  Code,
  BarChart3
} from 'lucide-react';
import { ScrollArea } from '../ui/scroll-area';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

// Menu structure with accordion groups
const menuGroups = [
  {
    id: 'principal',
    title: 'Principal',
    icon: LayoutDashboard,
    items: [
      { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    ]
  },
  {
    id: 'financeiro',
    title: 'Financeiro',
    icon: Wallet,
    items: [
      { name: 'Contas a Pagar', href: '/payables', icon: ArrowDownCircle },
      { name: 'Contas a Receber', href: '/receivables', icon: ArrowUpCircle },
      { name: 'Extrato Financeiro', href: '/extract', icon: FileBarChart },
      { name: 'Cartões de Crédito', href: '/credit-cards', icon: CreditCard },
      { name: 'Fluxo de Caixa', href: '/cash-flow', icon: TrendingUp },
      { name: 'Conciliação', href: '/reconciliation', icon: RefreshCcw },
      { name: 'Métricas de Conciliação', href: '/reconciliation/metrics', icon: BarChart3 },
      { name: 'Regras de Conciliação', href: '/reconciliation-rules', icon: Settings },
      { name: 'Open Finance', href: '/open-finance', icon: Link2 },
    ]
  },
  {
    id: 'operacional',
    title: 'Operacional',
    icon: ShoppingCart,
    items: [
      { name: 'Vendas', href: '/sales', icon: ShoppingCart },
      { name: 'Compras', href: '/purchases', icon: Package },
      { name: 'Contratos', href: '/contracts', icon: FileText },
      { name: 'Ordens de Serviço', href: '/service-orders', icon: ClipboardList },
      { name: 'Notas Fiscais', href: '/invoices', icon: Receipt },
    ]
  },
  {
    id: 'cadastros',
    title: 'Cadastros',
    icon: Users,
    items: [
      { name: 'Produtos', href: '/products', icon: Package },
      { name: 'Serviços', href: '/services', icon: FileText },
      { name: 'Entidades', href: '/entities', icon: Users },
      { name: 'Contas Bancárias', href: '/accounts', icon: Wallet },
      { name: 'Categorias', href: '/categories', icon: FolderTree },
      { name: 'Centros de Custo', href: '/cost-centers', icon: Building2 },
    ]
  },
  {
    id: 'relatorios',
    title: 'Relatórios',
    icon: FileBarChart,
    items: [
      { name: 'Relatórios', href: '/reports', icon: FileBarChart },
      { name: 'Orçamento', href: '/budget', icon: Target },
      { name: 'Integração Contábil', href: '/accounting', icon: Calculator },
      { name: 'Logs de Auditoria', href: '/audit-logs', icon: ClipboardList },
      { name: 'API & Webhooks', href: '/developer', icon: Code },
    ]
  },
];

// Logo URLs — simbolo isolado (fundo transparente) em ambos os estados
const LOGO_SYMBOL = "/ofs-logo.png";

// OFS Brand Colors for Sidebar (FIXED - doesn't change with theme)
const sidebarColors = {
  bg: '#0B1F33',           // Main background (ÚNICO para todo o menu)
  text: '#C9D6E2',         // Default text
  textHover: '#FFFFFF',    // Hover text
  icon: '#8FB9FF',         // Default icons
  accent: '#2DD4FF',       // OFS accent (active/highlight)
  bgActive: '#102A43',     // Active item background
  bgHover: '#0F2E4A',      // Hover background
};

export function Sidebar() {
  const { collapsed, toggleSidebar } = useSidebar();
  const [expandedGroup, setExpandedGroup] = useState(null);
  const location = useLocation();

  // Toggle accordion group - only one can be open at a time
  const toggleGroup = (groupId) => {
    if (collapsed) return;
    setExpandedGroup(expandedGroup === groupId ? null : groupId);
  };

  // Handle logo click to toggle sidebar
  const handleLogoClick = () => {
    toggleSidebar();
    if (!collapsed) {
      setExpandedGroup(null);
    }
  };

  // Check if any item in a group is active
  const isGroupActive = (group) => {
    return group.items.some(item => 
      location.pathname === item.href || location.pathname.startsWith(item.href + '/')
    );
  };

  return (
    <TooltipProvider delayDuration={0}>
      <aside
        data-testid="sidebar"
        data-tour="sidebar"
        className={cn(
          "fixed left-0 top-0 z-40 h-screen transition-all duration-300 ease-in-out",
          collapsed ? "w-[72px]" : "w-64"
        )}
        style={{ backgroundColor: sidebarColors.bg }}
      >
        {/* Logo Header - Clickable to toggle sidebar */}
        <div 
          className={cn(
            "flex items-center justify-center cursor-pointer transition-all duration-300 ease-in-out",
            collapsed ? "h-24 px-3" : "h-28 px-6"
          )}
          style={{ backgroundColor: sidebarColors.bg }}
          onClick={handleLogoClick}
          data-testid="sidebar-logo-toggle"
        >
          {collapsed ? (
            // Collapsed: Show only symbol, centered and larger
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center justify-center transition-transform duration-300 hover:scale-110">
                  <img
                    src={LOGO_SYMBOL}
                    alt="OFS - Organizador Financeiro Simples"
                    className="h-12 w-12 object-contain"
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent 
                side="right" 
                className="font-medium"
                style={{ backgroundColor: sidebarColors.bgActive, color: sidebarColors.textHover, border: 'none' }}
              >
                Expandir menu
              </TooltipContent>
            </Tooltip>
          ) : (
            // Expanded: symbol + brand name as text (logo image stays small/legible)
            <div className="flex items-center justify-center gap-2 w-full transition-transform duration-300 hover:scale-105">
              <img
                src={LOGO_SYMBOL}
                alt="OFS"
                className="h-12 w-12 object-contain flex-shrink-0"
              />
              <span className="text-white font-bold text-xl tracking-tight">OFS</span>
            </div>
          )}
        </div>

        {/* Navigation */}
        <ScrollArea className={cn("h-[calc(100vh-8rem)]", collapsed && "h-[calc(100vh-7rem)]")}>
          <nav className="px-3 py-4">
            {menuGroups.map((group, groupIndex) => {
              const isActive = isGroupActive(group);
              const isExpanded = expandedGroup === group.id;
              const GroupIcon = group.icon;

              // For single-item groups like Dashboard and Relatórios, render as direct link
              if (group.items.length === 1) {
                const item = group.items[0];
                const ItemIcon = item.icon;
                const itemActive = location.pathname === item.href;

                const linkContent = (
                  <NavLink
                    to={item.href}
                    className={cn(
                      "relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200 ease-in-out",
                      collapsed && "justify-center px-2"
                    )}
                    style={{
                      color: itemActive ? sidebarColors.accent : sidebarColors.text,
                      backgroundColor: itemActive ? sidebarColors.bgActive : 'transparent',
                    }}
                    onMouseEnter={(e) => {
                      if (!itemActive) {
                        e.currentTarget.style.backgroundColor = sidebarColors.bgHover;
                        e.currentTarget.style.color = sidebarColors.textHover;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!itemActive) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = sidebarColors.text;
                      }
                    }}
                  >
                    {/* Active indicator bar */}
                    {itemActive && (
                      <div 
                        className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 rounded-r-full"
                        style={{ backgroundColor: sidebarColors.accent }}
                      />
                    )}
                    <ItemIcon 
                      className="h-5 w-5 shrink-0 transition-colors duration-200"
                      style={{ color: itemActive ? sidebarColors.accent : sidebarColors.icon }}
                    />
                    {!collapsed && <span>{item.name}</span>}
                  </NavLink>
                );

                if (collapsed) {
                  return (
                    <div key={group.id} className="mb-1">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          {linkContent}
                        </TooltipTrigger>
                        <TooltipContent 
                          side="right" 
                          className="font-medium border-0"
                          style={{ backgroundColor: sidebarColors.bgActive, color: sidebarColors.textHover }}
                        >
                          {item.name}
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  );
                }

                return <div key={group.id} className="mb-1">{linkContent}</div>;
              }

              // For multi-item groups, render as accordion
              return (
                <div key={group.id} className={cn("mb-1", groupIndex > 0 && !collapsed && "mt-4")}>
                  {/* Group Header */}
                  {collapsed ? (
                    // Collapsed: Show icon with tooltip containing submenu
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          className={cn(
                            "relative flex w-full items-center justify-center rounded-lg px-2 py-2.5 text-sm font-medium transition-all duration-200 ease-in-out"
                          )}
                          style={{
                            color: isActive ? sidebarColors.accent : sidebarColors.text,
                            backgroundColor: isActive ? sidebarColors.bgActive : 'transparent',
                          }}
                          onMouseEnter={(e) => {
                            if (!isActive) {
                              e.currentTarget.style.backgroundColor = sidebarColors.bgHover;
                              e.currentTarget.style.color = sidebarColors.textHover;
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isActive) {
                              e.currentTarget.style.backgroundColor = 'transparent';
                              e.currentTarget.style.color = sidebarColors.text;
                            }
                          }}
                        >
                          {isActive && (
                            <div 
                              className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 rounded-r-full"
                              style={{ backgroundColor: sidebarColors.accent }}
                            />
                          )}
                          <GroupIcon 
                            className="h-5 w-5 shrink-0"
                            style={{ color: isActive ? sidebarColors.accent : sidebarColors.icon }}
                          />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent 
                        side="right" 
                        className="p-0 border-0 overflow-hidden"
                        style={{ backgroundColor: sidebarColors.bg }}
                      >
                        <div className="min-w-[180px]">
                          <p 
                            className="px-3 py-2 text-xs font-semibold uppercase tracking-wider"
                            style={{ backgroundColor: sidebarColors.bgActive, color: sidebarColors.text }}
                          >
                            {group.title}
                          </p>
                          <div className="py-1">
                            {group.items.map((item) => {
                              const itemActive = location.pathname === item.href;
                              return (
                                <NavLink
                                  key={item.href}
                                  to={item.href}
                                  className="block px-3 py-2 text-sm transition-colors duration-150"
                                  style={{
                                    color: itemActive ? sidebarColors.accent : sidebarColors.text,
                                    backgroundColor: itemActive ? sidebarColors.bgActive : 'transparent',
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!itemActive) {
                                      e.currentTarget.style.backgroundColor = sidebarColors.bgHover;
                                      e.currentTarget.style.color = sidebarColors.textHover;
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (!itemActive) {
                                      e.currentTarget.style.backgroundColor = 'transparent';
                                      e.currentTarget.style.color = sidebarColors.text;
                                    }
                                  }}
                                >
                                  {item.name}
                                </NavLink>
                              );
                            })}
                          </div>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    // Expanded: Show accordion header
                    <button
                      onClick={() => toggleGroup(group.id)}
                      className="relative flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200 ease-in-out"
                      style={{
                        color: isActive ? sidebarColors.accent : sidebarColors.text,
                        backgroundColor: isExpanded ? sidebarColors.bgHover : 'transparent',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = sidebarColors.bgHover;
                        e.currentTarget.style.color = sidebarColors.textHover;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = isExpanded ? sidebarColors.bgHover : 'transparent';
                        e.currentTarget.style.color = isActive ? sidebarColors.accent : sidebarColors.text;
                      }}
                    >
                      {isActive && (
                        <div 
                          className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 rounded-r-full"
                          style={{ backgroundColor: sidebarColors.accent }}
                        />
                      )}
                      <div className="flex items-center gap-3">
                        <GroupIcon 
                          className="h-5 w-5 shrink-0"
                          style={{ color: isActive ? sidebarColors.accent : sidebarColors.icon }}
                        />
                        <span>{group.title}</span>
                      </div>
                      <ChevronDown 
                        className={cn(
                          "h-4 w-4 shrink-0 transition-transform duration-200 ease-in-out",
                          isExpanded && "rotate-180"
                        )}
                        style={{ color: sidebarColors.icon }}
                      />
                    </button>
                  )}

                  {/* Group Items - Accordion content */}
                  {!collapsed && (
                    <div
                      className={cn(
                        "overflow-hidden transition-all duration-300 ease-in-out",
                        isExpanded ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                      )}
                    >
                      <ul className="mt-1 ml-7 space-y-0.5 pl-3" style={{ borderLeft: `1px solid ${sidebarColors.bgHover}` }}>
                        {group.items.map((item) => {
                          const ItemIcon = item.icon;
                          const itemActive = location.pathname === item.href || 
                            location.pathname.startsWith(item.href + '/');

                          return (
                            <li key={item.href}>
                              <NavLink
                                to={item.href}
                                className="relative flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] font-medium transition-all duration-200 ease-in-out"
                                style={{
                                  color: itemActive ? sidebarColors.accent : sidebarColors.text,
                                  backgroundColor: itemActive ? sidebarColors.bgActive : 'transparent',
                                }}
                                onMouseEnter={(e) => {
                                  if (!itemActive) {
                                    e.currentTarget.style.backgroundColor = sidebarColors.bgHover;
                                    e.currentTarget.style.color = sidebarColors.textHover;
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (!itemActive) {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.style.color = sidebarColors.text;
                                  }
                                }}
                              >
                                <ItemIcon 
                                  className="h-4 w-4 shrink-0"
                                  style={{ color: itemActive ? sidebarColors.accent : sidebarColors.icon }}
                                />
                                <span>{item.name}</span>
                              </NavLink>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </ScrollArea>

        {/* Settings at bottom - NO border, just spacing */}
        <div className="absolute bottom-0 left-0 right-0 px-3 py-3">
          {collapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <NavLink
                  to="/settings"
                  className="relative flex items-center justify-center rounded-lg px-2 py-2.5 text-sm font-medium transition-all duration-200 ease-in-out"
                  style={{
                    color: location.pathname === '/settings' ? sidebarColors.accent : sidebarColors.text,
                    backgroundColor: location.pathname === '/settings' ? sidebarColors.bgActive : 'transparent',
                  }}
                  onMouseEnter={(e) => {
                    if (location.pathname !== '/settings') {
                      e.currentTarget.style.backgroundColor = sidebarColors.bgHover;
                      e.currentTarget.style.color = sidebarColors.textHover;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (location.pathname !== '/settings') {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = sidebarColors.text;
                    }
                  }}
                >
                  {location.pathname === '/settings' && (
                    <div 
                      className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 rounded-r-full"
                      style={{ backgroundColor: sidebarColors.accent }}
                    />
                  )}
                  <Settings 
                    className="h-5 w-5 shrink-0"
                    style={{ color: location.pathname === '/settings' ? sidebarColors.accent : sidebarColors.icon }}
                  />
                </NavLink>
              </TooltipTrigger>
              <TooltipContent 
                side="right" 
                className="font-medium border-0"
                style={{ backgroundColor: sidebarColors.bgActive, color: sidebarColors.textHover }}
              >
                Configurações
              </TooltipContent>
            </Tooltip>
          ) : (
            <NavLink
              to="/settings"
              className="relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200 ease-in-out"
              style={{
                color: location.pathname === '/settings' ? sidebarColors.accent : sidebarColors.text,
                backgroundColor: location.pathname === '/settings' ? sidebarColors.bgActive : 'transparent',
              }}
              onMouseEnter={(e) => {
                if (location.pathname !== '/settings') {
                  e.currentTarget.style.backgroundColor = sidebarColors.bgHover;
                  e.currentTarget.style.color = sidebarColors.textHover;
                }
              }}
              onMouseLeave={(e) => {
                if (location.pathname !== '/settings') {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = sidebarColors.text;
                }
              }}
            >
              {location.pathname === '/settings' && (
                <div 
                  className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 rounded-r-full"
                  style={{ backgroundColor: sidebarColors.accent }}
                />
              )}
              <Settings 
                className="h-5 w-5 shrink-0"
                style={{ color: location.pathname === '/settings' ? sidebarColors.accent : sidebarColors.icon }}
              />
              <span>Configurações</span>
            </NavLink>
          )}
        </div>
      </aside>
    </TooltipProvider>
  );
}
